import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { img } from "../constants/assets";

function formatMonthYear(value, locale) {
  const [year, month] = String(value).split("-").map(Number);
  if (!year || !month) return value;

  const date = new Date(year, month - 1, 1);
  const monthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
  const capitalizedMonth =
    monthName.charAt(0).toLocaleUpperCase(locale) + monthName.slice(1);

  return `${capitalizedMonth} ${year}`;
}

function LegalPageLayout({ title, lastUpdated, navItems, navAriaLabel, children }) {
  const { t, i18n } = useTranslation();
  const formattedDate = formatMonthYear(lastUpdated, i18n.language);
  const navKey = navItems.map((item) => item.id).join("|");
  const [activeSection, setActiveSection] = useState(navItems[0]?.id);

  useEffect(() => {
    const ids = navKey.split("|");
    // Matches .legal-section scroll-margin-top so the highlight flips as a
    // heading settles under the sticky header.
    const offset = 120;

    const syncActiveSection = () => {
      const atPageBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;

      if (atPageBottom) {
        setActiveSection(ids[ids.length - 1]);
        return;
      }

      let current = ids[0];
      ids.forEach((id) => {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= offset) {
          current = id;
        }
      });
      setActiveSection(current);
    };

    syncActiveSection();
    window.addEventListener("scroll", syncActiveSection, { passive: true });
    window.addEventListener("resize", syncActiveSection);

    return () => {
      window.removeEventListener("scroll", syncActiveSection);
      window.removeEventListener("resize", syncActiveSection);
    };
  }, [navKey]);

  return (
    <section className="legal-page py-50">
      <div className="container">
        <div className="legal-layout">
          <aside className="legal-sidebar">
            <h1 className="legal-sidebar-title">{title}</h1>
            <p className="legal-sidebar-date">
              {t("common.lastUpdated", { date: formattedDate })}
            </p>

            <nav className="legal-nav" aria-label={navAriaLabel}>
              {navItems.map((item) => {
                const isActive = item.id === activeSection;

                return (
                  <a
                    key={item.id}
                    className={`legal-nav-link${
                      isActive ? " legal-nav-link--active" : ""
                    }`}
                    href={`#${item.id}`}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => setActiveSection(item.id)}
                  >
                    <img src={img(item.icon)} alt="" aria-hidden="true" />
                    {item.label}
                  </a>
                );
              })}
            </nav>
          </aside>

          <div className="legal-content">{children}</div>
        </div>
      </div>
    </section>
  );
}

export default LegalPageLayout;
